// The general ledger — real double-entry books.
//
// Every money event in the lab posts a JOURNAL: a set of lines whose debits equal its credits. That
// is the whole discipline, and it buys two things nothing else does — the accounts always balance,
// and every figure on a report can be traced back to the event that produced it.
//
// Two rules run through everything here:
//   • Nothing is edited or deleted. A mistake is fixed by posting a REVERSING journal, so what was
//     believed, and when, survives in the record.
//   • A closed month refuses new postings, so once figures are reported they cannot move.
//
// The doctor's statement (wf_ledger_entries) is unchanged and stays the customer-facing account.
// The ledger is the accounting behind it: the same events, read as books rather than as an account.
import { z } from 'zod';

export const GL_ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'] as const;
export type GlAccountType = (typeof GL_ACCOUNT_TYPES)[number];

export const GL_ACCOUNT_TYPE_LABELS: Record<GlAccountType, string> = {
  asset: 'Assets',
  liability: 'Liabilities',
  equity: 'Equity',
  income: 'Income',
  expense: 'Expenses',
};

/** Which side increases the account. Assets and expenses grow by debit; everything else by credit. */
export type GlNormalBalance = 'debit' | 'credit';

export const NORMAL_BALANCE_OF: Record<GlAccountType, GlNormalBalance> = {
  asset: 'debit',
  expense: 'debit',
  liability: 'credit',
  equity: 'credit',
  income: 'credit',
};

/** Which side of the balance sheet / P&L an account lands on. */
export const isBalanceSheetType = (t: GlAccountType): boolean => t !== 'income' && t !== 'expense';

export interface GlAccount {
  id: string;
  /** 1300, 4100 … sortable, and familiar to any accountant who opens the books. */
  code: string;
  name: string;
  type: GlAccountType;
  normalBalance: GlNormalBalance;
  description: string | null;
  isActive: boolean;
  /**
   * A system account is one the automatic posting rules name — receivable, revenue, the bank.
   * It can be renamed but never deactivated, because posting would break the next time an invoice
   * was raised.
   */
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export const GL_JOURNAL_SOURCES = ['invoice', 'payment', 'expense', 'manual', 'opening', 'reversal'] as const;
export type GlJournalSource = (typeof GL_JOURNAL_SOURCES)[number];

export const GL_JOURNAL_SOURCE_LABELS: Record<GlJournalSource, string> = {
  invoice: 'Invoice',
  payment: 'Payment',
  expense: 'Expense',
  manual: 'Manual entry',
  opening: 'Opening balance',
  reversal: 'Reversal',
};

export interface GlJournalLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  accountType: GlAccountType;
  /** Paisa. Exactly one of the two is non-zero on any line. */
  debit: number;
  credit: number;
  memo: string | null;
  /** Dimensions: whose line it is, so the ledger reads by doctor or by case without extra accounts. */
  doctorId: string | null;
  doctorName: string | null;
  caseId: string | null;
  caseNumber: string | null;
  sortOrder: number;
}

export interface GlJournal {
  id: string;
  journalNumber: string;
  /** The accounting date (YYYY-MM-DD) — which period the entry belongs to, not when it was typed. */
  entryDate: string;
  memo: string;
  source: GlJournalSource;
  sourceId: string | null;
  status: 'posted' | 'reversed';
  reversesId: string | null;
  reversedById: string | null;
  /** Paisa. Debits, which by construction equal credits. */
  total: number;
  createdByName: string | null;
  createdAt: string;
  lines: GlJournalLine[];
}

export interface GlPeriod {
  /** YYYY-MM. */
  period: string;
  status: 'open' | 'closed';
  closedAt: string | null;
  closedByName: string | null;
}

// ── Reports ──────────────────────────────────────────────────────────────────────────────────────

export interface TrialBalanceRow {
  accountId: string;
  code: string;
  name: string;
  type: GlAccountType;
  /** The account's net position, shown on whichever side it falls. One of these is always 0. */
  debit: number;
  credit: number;
}

export interface TrialBalance {
  from: string;
  to: string;
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  /** Debits == credits. False means something posted around the journal layer — investigate. */
  balanced: boolean;
}

export interface ReportLine {
  accountId: string;
  code: string;
  name: string;
  amount: number;
}

export interface IncomeStatement {
  from: string;
  to: string;
  income: ReportLine[];
  expense: ReportLine[];
  totalIncome: number;
  totalExpense: number;
  /** Income less expenses for the period. Negative is a loss. */
  netProfit: number;
}

export interface BalanceSheet {
  asOf: string;
  assets: ReportLine[];
  liabilities: ReportLine[];
  equity: ReportLine[];
  totalAssets: number;
  totalLiabilities: number;
  /** Equity accounts as posted, before this year's result is folded in. */
  totalEquity: number;
  /** Cumulative profit to date — never posted anywhere, always derived, so it can't drift. */
  retainedEarnings: number;
  /** Assets == liabilities + equity + retained earnings. */
  balanced: boolean;
}

export interface AccountLedgerRow {
  journalId: string;
  journalNumber: string;
  entryDate: string;
  memo: string;
  lineMemo: string | null;
  source: GlJournalSource;
  debit: number;
  credit: number;
  /** Running balance in the account's own normal direction. */
  balance: number;
}

export interface AccountLedger {
  account: GlAccount;
  from: string;
  to: string;
  opening: number;
  rows: AccountLedgerRow[];
  closing: number;
}

/** The books at a glance, for the accounting home screen. */
export interface AccountingSummary {
  cash: number;
  bank: number;
  receivable: number;
  payable: number;
  incomeThisMonth: number;
  expenseThisMonth: number;
  profitThisMonth: number;
  /** True when the trial balance for all time balances. */
  balanced: boolean;
  openPeriod: string;
  journalCount: number;
  /** Money events that have not produced a journal — should always be empty. */
  unpostedCount: number;
}

// ── Input schemas ────────────────────────────────────────────────────────────────────────────────

const money = z.coerce.number().int().min(0).max(1_000_000_000_00);
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');

export const glAccountSchema = z.object({
  code: z.string().trim().regex(/^\d{3,6}$/, 'An account code is 3–6 digits'),
  name: z.string().trim().min(2, 'Name the account').max(120),
  type: z.enum(GL_ACCOUNT_TYPES),
  description: z.string().trim().max(400).nullish(),
  isActive: z.boolean().optional(),
});
export type GlAccountInput = z.infer<typeof glAccountSchema>;

export const glJournalLineSchema = z
  .object({
    accountId: z.string().min(1, 'Pick an account'),
    debit: money.default(0),
    credit: money.default(0),
    memo: z.string().trim().max(300).nullish(),
    doctorId: z.string().nullish(),
    caseId: z.string().nullish(),
  })
  // A line that is both, or neither, is not a line — it is a mistake that would still balance.
  .refine((l) => (l.debit > 0) !== (l.credit > 0), {
    message: 'Each line is either a debit or a credit, and must be more than zero',
  });

export const glJournalSchema = z
  .object({
    entryDate: dateStr,
    memo: z.string().trim().min(2, 'Say what this entry is for').max(300),
    lines: z.array(glJournalLineSchema).min(2, 'A journal needs at least two lines'),
  })
  .refine(
    (j) => j.lines.reduce((a, l) => a + l.debit, 0) === j.lines.reduce((a, l) => a + l.credit, 0),
    { message: 'Debits and credits must be equal', path: ['lines'] },
  );
export type GlJournalInput = z.infer<typeof glJournalSchema>;

/**
 * An expense: what was spent, on what, and out of which pot. Two lines fall out of it — debit the
 * expense account, credit the cash or bank it left — so the form is a form, not a journal.
 */
export const glExpenseSchema = z.object({
  entryDate: dateStr,
  accountId: z.string().min(1, 'Which expense is it?'),
  /**
   * Where the money came from — or, for a bill not yet paid, accounts payable. Crediting a
   * liability instead of cash is the whole of "on credit": the expense is recognised when it is
   * incurred, and the payment is a separate event later.
   */
  paidFromAccountId: z.string().min(1, 'Which account did it come out of?'),
  amount: z.coerce.number().int().positive('Enter the amount').max(1_000_000_000_00),
  payee: z.string().trim().max(160).nullish(),
  reference: z.string().trim().max(120).nullish(),
  memo: z.string().trim().min(2, 'What was it for?').max(300),
});
export type GlExpenseInput = z.infer<typeof glExpenseSchema>;

/**
 * What the lab already had on the day it started keeping these books.
 *
 * Every real business has cash, equipment and money owed to it before the software exists. Entering
 * those as an opening journal is what makes the first balance sheet true; without it every report
 * describes a lab that sprang into existence with nothing.
 *
 * Whatever the balances don't account for goes to owner's capital, which is the correct home for it:
 * the difference between what the business has and what it owes IS the owner's stake.
 */
export const glOpeningSchema = z.object({
  entryDate: dateStr,
  balances: z
    .array(
      z.object({
        accountId: z.string().min(1),
        /** Paisa, in the account's own normal direction. Negative flips it to the other side. */
        amount: z.coerce.number().int().min(-1_000_000_000_00).max(1_000_000_000_00),
      }),
    )
    .min(1, 'Enter at least one balance'),
});
export type GlOpeningInput = z.infer<typeof glOpeningSchema>;

/** Paying a supplier: what the lab owed goes down, and so does the cash it paid with. */
export const glSupplierPaymentSchema = z.object({
  entryDate: dateStr,
  amount: z.coerce.number().int().positive('Enter the amount').max(1_000_000_000_00),
  paidFromAccountId: z.string().min(1, 'Which account did it come out of?'),
  payee: z.string().trim().min(1, 'Who was paid?').max(160),
  reference: z.string().trim().max(120).nullish(),
});
export type GlSupplierPaymentInput = z.infer<typeof glSupplierPaymentSchema>;

export const glReverseSchema = z.object({
  reason: z.string().trim().min(3, 'Why is this being reversed?').max(300),
  /** Post the reversal on a later date when the original month is already closed. */
  entryDate: dateStr.nullish(),
});

export const glPeriodSchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use a YYYY-MM period'),
  status: z.enum(['open', 'closed']),
});

/** YYYY-MM for a date, which is how a posting finds the period it belongs to. */
export const periodOf = (isoDate: string): string => isoDate.slice(0, 7);
